export const Sentry = {
  enabled: import.meta.env.VITE_SENTRY_DSN !== undefined
};

export const Permissions = {
  scoreReports: "score_reports"
};

export const FindingTypes = {
  strength: "Strength",
  risk: "Risk",
  opportunity: "Opportunity"
};

export const FindingRisks = {
  notAvailable: "N/A"
};

export const RecommendationPriorities = {
  low: "low",
  medium: "medium",
  high: "high",
  critical: "critical"
};

export const HighRecommendationPriorities = [RecommendationPriorities.high, RecommendationPriorities.critical];

export const ReportOutputs = {
  mainReport: "main_report",
  preliminaryReport: "preliminary_report",
  supportingAnalysis: "supporting_analysis",
  supportingAnalysisExtended: "supporting_analysis_extended",
  technicalDueDiligence: "ppt_pre",
  atAGlance: "at_a_glance",
  comprehensive: "comprehensive"
};

const WORD_OUTPUTS = [
  ReportOutputs.mainReport,
  ReportOutputs.preliminaryReport,
  ReportOutputs.supportingAnalysis,
  ReportOutputs.supportingAnalysisExtended,
  ReportOutputs.atAGlance,
  ReportOutputs.comprehensive
];

/**
 * Returns true when PPT is selected and no Word report output is present.
 * When a Word output is added alongside PPT, fields should remain visible.
 */
export const isPptOnlyOutput = (reportOutputs?: string[] | null): boolean => {
  if (!reportOutputs?.includes(ReportOutputs.technicalDueDiligence)) return false;
  return !reportOutputs.some((output) => WORD_OUTPUTS.includes(output));
};

export const ReportStatus = {
  new: "new",
  reviewCompleted: "review_completed",
  published: "published"
};

export const ReportRoleNames = {
  project_lead: "Project Lead",
  executive_sponsor: "Executive Sponsor",
  client_recipient: "Client recipient",
  project_management: "Project Management",
  reviewers: "REVIEWERS",
  other_contributors: "Other contributors"
};

export const FileTypes = {
  preliminaryDiligenceReport: "preliminary_diligence_report",
  customSpecialReport: "custom_special_report",
  clientReport: "client_report",
  image: "image",
  reportTypes: ["report_c360", "report_supporting_analysis", "report_at_a_glance",
    "report_preliminary", "client_report", "report_technical_due_diligence_report"]
};

export const FileAvailabilities = [
  { key: 0, text: "Visible on Publish" },
  { key: -1, text: "Hidden" },
  { key: 1, text: "Always Visible" }
];

export const RoleNames = {
  client: "client"
};

export const EmphasisTiers = {
  essential: 1,
  additional: 2
};

export const IndicatorVisibility = {
  scored: "SCORED",
  unscored: "UNSCORED"
};

export const CheckboxStatus = {
  noChange: 1,
  change: 2
};

class FieldInclusion {
  constructor(public includeFields: string[] = [], private _includeAll = true) { }

  get includeAll() {
    // return false if any specific fields, else return the eplicitly set value
    return this.includeFields.length > 0 ? false : this._includeAll;
  }

  set includeAll(_includeAll) {
    this._includeAll = _includeAll;
  }
}

export class PPTFieldInclusions {
  cost_analysis: FieldInclusion;
  background: FieldInclusion;
  scope: FieldInclusion;
  executive_summary: FieldInclusion;
  overall_evaluation: FieldInclusion;
  supplementary_analysis: FieldInclusion;
  themes: FieldInclusion;
  key_findings: FieldInclusion;
  key_recommendations: FieldInclusion;
  key_metrics: FieldInclusion;
  finding: FieldInclusion;
  recommendation: FieldInclusion;
  metrics: FieldInclusion;
  report_findings: FieldInclusion;
  report_recommendations: FieldInclusion;
  report_track_findings: FieldInclusion;
  report_track_recommendations: FieldInclusion;
  technology_and_tools: FieldInclusion;
  technical_debt: FieldInclusion;

  constructor() {
    this.cost_analysis = new FieldInclusion([], false);
    this.background = new FieldInclusion([], false);
    this.scope = new FieldInclusion([], false);
    this.executive_summary = new FieldInclusion([]);
    this.overall_evaluation = new FieldInclusion(["ISEDRating"], false);
    this.supplementary_analysis = new FieldInclusion([]);
    this.themes = new FieldInclusion(["description", "addressability", "risk", "linkedFindings"], false);
    this.key_findings = new FieldInclusion([], false);
    this.key_recommendations = new FieldInclusion([]);
    this.key_metrics = new FieldInclusion([]);
    this.finding = new FieldInclusion(["findingFull", "type", "risk", "topic", "renderBusinessImpactColumn"], false);
    this.recommendation = new FieldInclusion(["recommendationFull", "businessValue", "priority", "complexity", "duration", "costsTimingAssumptions"], false);
    this.metrics = new FieldInclusion(["metric", "value", "expected", "implications"], false);
    this.report_findings = new FieldInclusion([], false);
    this.report_recommendations = new FieldInclusion([], false);
    this.report_track_findings = new FieldInclusion([]);
    this.report_track_recommendations = new FieldInclusion([]);
    this.technology_and_tools = new FieldInclusion(["category", "items"], false);
    this.technical_debt = new FieldInclusion([], false);
  }
}

export const pptFieldInclusions = new PPTFieldInclusions();
export const supportUrl = "https://get.help.crosslaketech.com/servicedesk/customer/portal/14/group/63/create/137";
